import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

// TOTP Input Component - Minimalist Style
const TOTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);

  useEffect(() => {
    const digitsArray = value.split('').slice(0, 6);
    while (digitsArray.length < 6) {
      digitsArray.push('');
    }
    setDigits(digitsArray);
  }, [value]);

  const handleChange = (index, newValue) => {
    if (newValue.length > 1) {
      newValue = newValue.slice(-1);
    }

    if (!/^\d*$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    const totpValue = newDigits.join('');
    onChange(totpValue);

    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (pastedDigits.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedDigits.length && i < 6; i++) {
        newDigits[i] = pastedDigits[i];
      }
      setDigits(newDigits);
      onChange(pastedDigits);

      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 600,
              fontFamily: 'Metropolis, monospace',
              color: '#253E66',
              padding: '12px 0',
            }
          }}
          sx={{
            width: 52,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              '& fieldset': {
                borderColor: '#E5E5E5',
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: '#D9DBDC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4698D3',
                borderWidth: 2,
              },
              '&.Mui-disabled fieldset': {
                borderColor: '#E5E5E5',
              },
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#BDBDBD',
            },
          }}
        />
      ))}
    </Box>
  );
};

const MFASetup = ({ setupData, onComplete, onError }) => {
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [qrLoading, setQrLoading] = useState(true);

  const handleQrLoad = () => {
    setQrLoading(false);
  };

  const handleQrError = () => {
    setQrLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.completeMFASetup(totpCode);
      if (response.success) {
        toast.success('MFA setup completed successfully');
        onComplete();
      } else {
        toast.error(response.message || 'MFA setup failed');
      }
    } catch (err) {
      console.error('MFA setup error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to complete MFA setup';
      toast.error(errorMessage);
      if (onError) {
        onError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Secret copied to clipboard');
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  if (!setupData) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
          MFA setup data is missing. Please try again.
        </Typography>
      </Alert>
    );
  }

  const qrCodeUrl = setupData.mfaSetupQR || setupData.qrCodeUrl;
  const secret = setupData.mfaSecret || setupData.secret;

  const getQRCodeImageUrl = () => {
    if (!qrCodeUrl) return null;

    if (qrCodeUrl.startsWith('data:') || qrCodeUrl.startsWith('http')) {
      return qrCodeUrl;
    }

    if (qrCodeUrl.startsWith('otpauth://')) {
      const encodedUrl = encodeURIComponent(qrCodeUrl);
      return `https://quickchart.io/qr?text=${encodedUrl}&size=200&margin=2&ecLevel=M&dark=253E66&light=ffffff&format=png`;
    }

    return null;
  };

  const qrImageUrl = getQRCodeImageUrl();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
    <Box>
      {/* Header */}
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          color: '#253E66',
          mb: 1,
        }}
      >
        Setup MFA
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Metropolis, sans-serif',
          color: '#999999',
          fontSize: '0.9rem',
          mb: 3,
        }}
      >
        Secure your account with two-factor authentication
      </Typography>

      {/* Instructions */}
      <Box
        sx={{
          backgroundColor: '#F9FBFD',
          border: '1px solid #E5E5E5',
          borderRadius: '6px',
          p: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            fontSize: '0.85rem',
            mb: 1,
          }}
        >
          Setup Steps:
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.85rem',
            color: '#666666',
            lineHeight: 1.6,
          }}
        >
          1. Install an authenticator app (Google Authenticator, Authy)
          <br />
          2. Scan the QR code or enter the secret key manually
          <br />
          3. Enter the 6-digit code from your app below
        </Typography>
      </Box>

      {/* QR Code Section */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E5E5',
          borderRadius: '6px',
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            fontSize: '0.9rem',
            mb: 2,
          }}
        >
          Scan QR Code
        </Typography>

        {qrImageUrl ? (
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              padding: 2,
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              border: '1px solid #E5E5E5',
              minWidth: 200,
              minHeight: 200,
            }}
          >
            {qrLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={40} sx={{ color: '#4698D3' }} />
              </Box>
            )}
            <img
              src={qrImageUrl}
              alt="MFA QR Code"
              onLoad={handleQrLoad}
              onError={handleQrError}
              style={{
                width: '180px',
                height: '180px',
                display: 'block',
                opacity: qrLoading ? 0 : 1,
                transition: 'opacity 0.3s ease',
              }}
            />
          </Box>
        ) : (
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#999999',
              fontSize: '0.85rem',
            }}
          >
            QR code not available. Use manual entry below.
          </Typography>
        )}
      </Paper>
      </motion.div>

      {/* Manual Entry Section */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E5E5',
          borderRadius: '6px',
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            fontSize: '0.9rem',
            mb: 1,
          }}
        >
          Manual Entry
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            color: '#999999',
            fontSize: '0.85rem',
            mb: 2,
          }}
        >
          Enter this secret key in your authenticator app:
        </Typography>

        {secret ? (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#F9FBFD',
              borderRadius: '6px',
              border: '1px solid #E5E5E5',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                color: '#253E66',
                fontWeight: 600,
                letterSpacing: '0.05em',
                mb: 2,
                textAlign: 'center',
              }}
            >
              ••••••••••••••••••••
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => copyToClipboard(secret)}
                sx={{
                  color: secretCopied ? '#27AE60' : '#4698D3',
                  borderColor: secretCopied ? '#27AE60' : '#E5E5E5',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: secretCopied ? '#27AE60' : '#4698D3',
                    backgroundColor: 'rgba(70, 152, 211, 0.04)',
                  },
                }}
                startIcon={secretCopied ? <CheckIcon /> : <CopyIcon />}
              >
                {secretCopied ? 'Copied!' : 'Copy Secret'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#999999',
              fontSize: '0.85rem',
            }}
          >
            Secret key not available
          </Typography>
        )}
      </Paper>
      </motion.div>

      {/* Verification Form */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
      <Box component="form" onSubmit={handleVerifyCode}>
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            textAlign: 'center',
            mb: 2,
            fontSize: '0.95rem',
          }}
        >
          Enter Verification Code
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TOTPInput
            value={totpCode}
            onChange={(value) => setTotpCode(value)}
            disabled={loading}
          />

          <Typography
            sx={{
              color: '#999999',
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.75rem',
              textAlign: 'center',
              mt: 2,
            }}
          >
            Enter the 6-digit code from your authenticator app
          </Typography>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || totpCode.length !== 6}
          sx={{
            backgroundColor: '#4698D3',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#3580B8',
            },
            '&:disabled': {
              backgroundColor: '#E5E5E5',
              color: '#BDBDBD',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Complete Setup'
          )}
        </Button>
      </Box>
      </motion.div>
    </Box>
    </motion.div>
  );
};

export default MFASetup;
